import { Inject, Injectable } from '@angular/core';
import { ChatEventsNameE } from '@enums/events/chat-events-name';
import { UserHelperService } from '@helpers/user-helper.service';
import { ContactI } from '@interfaces/contact.interface';
import { ChangeStatusMessageI } from '@interfaces/message.interface';
import { UserAttributesI, UserSocketLoginI } from '@interfaces/user.interface';
import { BehaviorSubject, Subject } from 'rxjs';
import { Socket } from 'socket.io-client';
import { SocketService } from '@events/chat/socket.service';
import { GroupI, GroupStepI, NewMessageSocket } from '@interfaces/group.interface';
import { GroupEventsNameE } from '@enums/events/group-events-name';

@Injectable({
  providedIn: 'root',
})
export class GroupSocketService {
  private updateMessageStatusOnCreateSubject = new BehaviorSubject<ChangeStatusMessageI | null>(null);
  private newMessageSubject = new BehaviorSubject<NewMessageSocket | null>(null);
  private newChatSubject = new BehaviorSubject<GroupI | null>(null);
  private removeGroupSubject = new BehaviorSubject<GroupI | null>(null);
  private contactRemovedSubject = new BehaviorSubject<ContactI | null>(null);
  private updateMessageStatusSubject = new BehaviorSubject<any>(null);
  private newGroupSubject = new BehaviorSubject<GroupI | null>(null);
  private syncGroupSubject = new BehaviorSubject<string | null>(null);
  private updateAllMessageStatusSubject = new BehaviorSubject<any>(null);
  private updateGroupSubject = new BehaviorSubject<GroupI | null>(null);
  private deleteGroupSubject = new BehaviorSubject<string>('');
  private groupCreateFailedSubject = new Subject<Pick<GroupI, '_id' | 'subject'>>();
  private canRefreshGroupSubject = new BehaviorSubject<boolean>(false);
  private updateGroupParticipantsSubject = new BehaviorSubject<string | null>(null);
  private createGroupStepCallInitiatedSubject = new BehaviorSubject<GroupStepI | null>(null);
  private newGroupMentions = new BehaviorSubject<number | null>(null);

  private socket!: Socket;

  public messageUpdatedOnCreate$ = this.updateMessageStatusOnCreateSubject.asObservable();
  public newMessage$ = this.newMessageSubject.asObservable();
  public newChat$ = this.newChatSubject.asObservable();
  public newGroup$ = this.newGroupSubject.asObservable();
  public syncGroup$ = this.syncGroupSubject.asObservable();
  public contactDeleted$ = this.contactRemovedSubject.asObservable();
  public removeChat$ = this.removeGroupSubject.asObservable();
  public updateMessage$ = this.updateMessageStatusSubject.asObservable();
  public updateAllMessage$ = this.updateAllMessageStatusSubject.asObservable();
  public updateGroup$ = this.updateGroupSubject.asObservable();
  public deleteGroup$ = this.deleteGroupSubject.asObservable();
  public groupCreateFailed$ = this.groupCreateFailedSubject.asObservable();
  public canRefreshGroup$ = this.canRefreshGroupSubject.asObservable();
  public updateGroupParticipants$ = this.updateGroupParticipantsSubject.asObservable();
  public createGroupStepCallInitiatedSubject$ = this.createGroupStepCallInitiatedSubject.asObservable();
  public newGroupMentions$ = this.newGroupMentions.asObservable();

  private groupConnect: string[] = [];
  private channelConnect: string[] = [];

  private isGroupListenersInitialized: boolean = false;

  constructor(
    @Inject(UserHelperService) private userHelperService: UserHelperService,
    private socketService: SocketService
  ) {}

  public init(options?: { connectionCallback?: Function }) {
    if (this.userHelperService.getUserAttributes()) {
      this.initGroup(options?.connectionCallback);
    }
  }

  public async initGroup(connectionCallback?: Function) {
    this.socket && this.socketService.restart();
    this.socket = this.socketService.init();

    if (!this.isGroupListenersInitialized) {
      this.listenAllEvents();
      this.isGroupListenersInitialized = true;
    }

    if (connectionCallback) {
      this.onConnect(() => {
        connectionCallback();
      });
    }
  }

  public getUserData(user: UserAttributesI | void): UserSocketLoginI | void {
    if (!user) return;
    return {
      _id: user['custom:userId'],
      customerId: user['custom:customer'],
      teams: user['custom:team'],
      team: user['custom:team'],
    } as UserSocketLoginI;
  }

  private listenAllEvents(): void {
    this.listenUpdateMessage();
    this.listenNewGroup();
    this.listenSyncGroup();
    this.listenNewChat();
    this.listenNewMessage();
    this.listenUpdateGroup();
    this.listenDeleteGroup();
    this.listenGroupCreateFailed();
    this.listenUpdateGroupParticipants();
    this.listenCreateGroupStepCallInitiated();
    this.listenNewGroupMentions();

    this.listenContactRemoved();
    this.listenDeleteMessageFromContact();
    this.listenMessageTranscription();
  }

  private listenDeleteMessageFromContact() {
    try {
      this.socket.on(GroupEventsNameE.DELETE_MESSAGE, (data) => {
        this.updateAllMessageStatusSubject.next(data);
      });
    } catch (error) {
      throw new Error("Can't listen update message event");
    }
  }

  private listenSyncGroup(): void {
    try {
      this.socket.on(GroupEventsNameE.SYNC_GROUP, (data) => {
        this.syncGroupSubject.next(data);
      });
    } catch (error) {
      throw new Error("Can't listen sync-group event");
    }
  }

  private listenNewGroup(): void {
    try {
      this.socket.on(GroupEventsNameE.NEW_GROUP, (data) => {
        this.newGroupSubject.next(data);
      });
    } catch (error) {
      throw new Error("Can't listen new-group event");
    }
  }

  private listenNewMessage() {
    try {
      this.socket.on(GroupEventsNameE.MESSAGE_GROUP, (data) => {
        this.newMessageSubject.next(data);
      });
    } catch (error) {
      throw new Error("Can't listen update message event");
    }
  }

  private listenUpdateMessage() {
    try {
      this.socket.on(GroupEventsNameE.MESSAGE_STATUS_CHANGE, (data) => {
        this.updateMessageStatusSubject.next(data);
      });
    } catch (error) {
      throw new Error("Can't listen update message event");
    }
  }

  public listenMessageTranscription(): void {
    try {
      this.socket.on(GroupEventsNameE.MESSAGE_TRANSCRIPTION_GROUP, (data) => {
        this.updateAllMessageStatusSubject.next(data);
      });
    } catch (error) {
      throw new Error("Can't listen message-transcription-group event");
    }
  }

  private listenContactRemoved(): void {
    try {
      this.socket.on(ChatEventsNameE.CONTACT_REMOVED, (data) => {
        this.contactRemovedSubject.next(data);
      });
    } catch (error) {
      throw new Error("Can't listen contact-removed event");
    }
  }

  public listenChannelRooms(id: string): void {
    try {
      this.channelConnect.push(id);
      this.socket.emit(GroupEventsNameE.JOIN_ROOMS, [id]);
    } catch (error) {
      throw new Error("Can't list all channels rooms");
    }
  }

  public listAllGroupRooms(groups: GroupI[]): void {
    try {
      groups.forEach((group) => {
        this.groupConnect.push(group._id);
        this.socket.emit(GroupEventsNameE.JOIN_ROOMS, [group._id]);
      });
    } catch (error) {
      throw new Error("Can't list all chats rooms");
    }
  }

  private listenNewChat(): void {
    try {
      this.socket.on(ChatEventsNameE.NEW_CHAT, (data) => {
        this.newChatSubject.next(data);
      });
    } catch (error) {
      throw new Error("Can't listen new-chat event");
    }
  }

  public leaveConnect(): void {
    this.socket.emit(GroupEventsNameE.LEAVE_ROOMS, [...this.groupConnect, ...this.channelConnect]);
  }

  private listenUpdateGroup(): void {
    try {
      this.socket.on(GroupEventsNameE.UPDATE_GROUP, (data) => {
        this.updateGroupSubject.next(data);
      });
    } catch (error) {
      throw new Error("Can't listen update-group event");
    }
  }

  private listenDeleteGroup(): void {
    try {
      this.socket.on(GroupEventsNameE.DELETE_GROUP, (groupId) => {
        this.deleteGroupSubject.next(groupId);
      });
    } catch (error) {
      throw new Error("Can't listen delete-group event");
    }
  }

  private listenGroupCreateFailed(): void {
    try {
      this.socket.on(GroupEventsNameE.GROUP_CREATE_FAILED, (group) => {
        this.groupCreateFailedSubject.next(group);
      });
    } catch (error) {
      throw new Error("Can't listen group-create-failed event");
    }
  }

  private listenUpdateGroupParticipants(): void {
    try {
      this.socket.on(GroupEventsNameE.UPDATE_GROUP_PARTICIPANTS, (data) => {
        this.updateGroupParticipantsSubject.next(data);
      });
    } catch (error) {
      throw new Error("Can't listen update-group-participants event");
    }
  }

  private listenCreateGroupStepCallInitiated(): void {
    try {
      this.socket.on(GroupEventsNameE.CREATE_GROUP_STEP_CALL_INITIATED, (data) => {
        this.createGroupStepCallInitiatedSubject.next(data);
      });
    } catch (error) {
      throw new Error(`Can't listen ${GroupEventsNameE.CREATE_GROUP_STEP_CALL_INITIATED} event`);
    }
  }

  private listenNewGroupMentions(): void {
    try {
      this.socket.on(GroupEventsNameE.NEW_GROUP_MENTIONS, (data) => {
        this.newGroupMentions.next(data);
      });
    } catch (error) {
      throw new Error(`Can't listen ${GroupEventsNameE.NEW_GROUP_MENTIONS} event`);
    }
  }

  public userLogout(): void {
    try {
      this.socket?.off('disconnect');
      this.socket?.off('connect');
      this.socket?.disconnect();
    } catch (error) {
      console.error(error);
      throw new Error("Can't logout user");
    }
  }

  public restart() {
    this.init();
  }

  public onDisconnect(callback: Function) {
    return this.createSocketEvent('disconnect', () => {
      callback();
    });
  }

  public onConnect(callback: Function) {
    return this.createSocketEvent('connect', () => {
      callback();
    });
  }

  public onConnectFailed(callback: Function) {
    return this.createSocketEvent('connect_failed', () => {
      callback();
    });
  }

  public onError(callback: Function) {
    return this.createSocketEvent('error', () => {
      callback();
    });
  }

  private async createSocketEvent(event: string, handler) {
    try {
      this.socket.off(event);
      this.socket.on(event, async (...args) => {
        await handler(...args);
      });
    } catch (error) {
      throw new Error(`Can't listen ${event} event`);
    }
  }

  public setRefreshGroup(canRefreshChat: boolean) {
    this.canRefreshGroupSubject.next(canRefreshChat);
  }
}
